/*
 * rageRoomController.js
 * -----------------------------------------------------------------------------
 * Controller factory for Rage Room API endpoints.
 *
 * Responsibilities:
 * - Encapsulates all admin and public rage room operations.
 * - Keeps endpoint-specific logic isolated from components/hooks.
 *
 * Usage:
 *   import createRageRoomController from "./controllers/rageRoomController";
 *   const rageRoom = createRageRoomController({ baseApiCall, parseQueryParams });
 *   await rageRoom.admin.listRooms({ locationId: "loc-123", query: { pageSize: 25 } });
 */

/**
 * Factory function that builds the Rage Room controller.
 *
 * @param {Object} deps - External dependencies.
 * @param {function(string, Object=): Promise<any>} deps.baseApiCall - Shared API caller.
 * @param {function(Object=): string} deps.parseQueryParams - Helper to stringify query params.
 * @returns {Object} Structured controller with admin and public namespaces.
 */
const createRageRoomController = ({ baseApiCall, parseQueryParams }) => {
	const adminBasePath = (locationId) => `/admin/locations/${locationId}/rage-rooms`;
	const schedulePath = (locationId, roomId) => `${adminBasePath(locationId)}/${roomId}/schedule`;

	return {
		admin: {
			/**
			 * Creates a new rage room under a specific location.
			 *
			 * Backend: POST /admin/locations/:locationId/rage-rooms
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {Object} params.payload - Body payload describing the rage room.
			 */
			createRoom: async ({ locationId, payload }) => {
				return await baseApiCall(adminBasePath(locationId), {
					method: "POST",
					body: payload
				});
			},

			/**
			 * Lists rage rooms for a location with optional filters/pagination.
			 *
			 * Backend: GET /admin/locations/:locationId/rage-rooms
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {Object} [params.query] - Query parameters (pageNumber, city, isActive, etc.).
			 */
			listRooms: async ({ locationId, query }) => {
				const queryString = parseQueryParams(query);
				return await baseApiCall(`${adminBasePath(locationId)}${queryString}`);
			},

			/**
			 * Retrieves a single rage room.
			 *
			 * Backend: GET /admin/locations/:locationId/rage-rooms/:roomId
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {string} params.roomId - Room identifier.
			 */
			getRoom: async ({ locationId, roomId }) => {
				return await baseApiCall(`${adminBasePath(locationId)}/${roomId}`);
			},

			/**
			 * Updates an existing rage room.
			 *
			 * Backend: PUT /admin/locations/:locationId/rage-rooms/:roomId
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {string} params.roomId - Room identifier.
			 * @param {Object} params.payload - Fields to update.
			 */
			updateRoom: async ({ locationId, roomId, payload }) => {
				return await baseApiCall(`${adminBasePath(locationId)}/${roomId}`, {
					method: "PUT",
					body: payload
				});
			},

			/**
			 * Soft-deletes a rage room.
			 *
			 * Backend: DELETE /admin/locations/:locationId/rage-rooms/:roomId
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {string} params.roomId - Room identifier.
			 */
			deleteRoom: async ({ locationId, roomId }) => {
				return await baseApiCall(`${adminBasePath(locationId)}/${roomId}`, {
					method: "DELETE"
				});
			},

			/**
			 * Creates or updates a rage room schedule for a specific date.
			 *
			 * Backend: POST /admin/locations/:locationId/rage-rooms/:roomId/schedule
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {string} params.roomId - Room identifier.
			 * @param {Object} params.payload - Schedule payload (date, sessions, etc.).
			 */
			upsertSchedule: async ({ locationId, roomId, payload }) => {
				return await baseApiCall(schedulePath(locationId, roomId), {
					method: "POST",
					body: payload
				});
			},

			/**
			 * Lists schedules for a rage room.
			 *
			 * Backend: GET /admin/locations/:locationId/rage-rooms/:roomId/schedule
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {string} params.roomId - Room identifier.
			 * @param {Object} [params.query] - Query params (date, startDate, endDate, pagination).
			 */
			listSchedule: async ({ locationId, roomId, query }) => {
				const queryString = parseQueryParams(query);
				return await baseApiCall(`${schedulePath(locationId, roomId)}${queryString}`);
			}
		},

		public: {
			/**
			 * Lists available rage room slots for public consumption.
			 *
			 * Backend: GET /rage-rooms/availability
			 *
			 * @param {Object} [query] - Filters such as city, roomId, date, etc.
			 */
			listAvailability: async (query) => {
				const queryString = parseQueryParams(query);
				return await baseApiCall(`/rage-rooms/availability${queryString}`);
			},

			/**
			 * Creates a public booking (Twilio verification handled by backend).
			 *
			 * Backend: POST /rage-rooms/bookings
			 *
			 * @param {Object} payload - Booking payload (locationId, roomId, date, session info, customer, etc.).
			 */
			bookRoom: async (payload) => {
				return await baseApiCall("/rage-rooms/bookings", {
					method: "POST",
					body: payload
				});
			}
		}
	};
};

export default createRageRoomController;